// src/pages/login/login-shell.hooks.ts
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
    LoginShellData,
    LoginStepId,
    LoginStepRegistry,
} from "./login-shell.types";

function isValidStepId(id: string | null | undefined): id is LoginStepId {
    return (
        id === "biometric-login" ||
        id === "biometric-error" ||
        id === "email-login" ||
        id === "password-login" ||
        id === "forgot-password" ||
        id === "verify-reset-code" ||
        id === "reset-password" ||
        id === "waitlist" ||
        id === "waitlist-sent" ||
        id === "waitlist-confirmed"
    );
}

function readQueryStep(urlKey: string): LoginStepId | null {
    try {
        const url = new URL(window.location.href);
        const step = url.searchParams.get(urlKey);
        return isValidStepId(step) ? step : null;
    } catch {
        return null;
    }
}

function writeQueryStep(
    urlKey: string,
    step: LoginStepId,
    mode: "push" | "replace",
) {
    try {
        const url = new URL(window.location.href);
        url.searchParams.set(urlKey, step);
        if (mode === "replace") {
            window.history?.replaceState({}, "", url.toString());
        } else {
            window.history.pushState({}, "", url.toString());
        }
    } catch {
        // ignore
    }
}

export function useLoginShell(args: {
    initialStep: LoginStepId;
    registry: LoginStepRegistry;
    syncToUrl: boolean;
    urlKey: string;
}) {
    const { initialStep, registry, syncToUrl, urlKey } = args;

    // internal stack (string-based history, not tied to browser history)
    const stackRef = useRef<LoginStepId[]>([]);
    const didInitRef = useRef(false);

    const [data, setDataState] = useState<LoginShellData>({});
    const setData = useCallback((patch: Partial<LoginShellData>) => {
        setDataState((prev) => ({ ...prev, ...patch }));
    }, []);

    const firstStep = useMemo<LoginStepId>(() => {
        if (!syncToUrl) return initialStep;
        return readQueryStep(urlKey) ?? initialStep;
    }, [initialStep, syncToUrl, urlKey]);

    const [stepId, setStepId] = useState<LoginStepId>(firstStep);

    // init stack (StrictMode-safe)
    useEffect(() => {
        if (didInitRef.current) return;
        didInitRef.current = true;

        stackRef.current = [firstStep];
        if (syncToUrl) writeQueryStep(urlKey, firstStep, "replace");
    }, [firstStep, syncToUrl, urlKey]);

    // optional: respond to browser back/forward if syncToUrl is enabled
    useEffect(() => {
        if (!syncToUrl) return;

        const onPopState = () => {
            const s = readQueryStep(urlKey);
            if (s) {
                setStepId(s);

                // keep internal stack coherent: push if different
                const st = stackRef.current;
                const last = st[st.length - 1];
                if (last !== s) st.push(s);
            }
        };

        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, [syncToUrl, urlKey]);

    const go = useCallback(
        (to: LoginStepId) => {
            if (!registry[to]) return;
            setStepId(to);
            stackRef.current.push(to);
            if (syncToUrl) writeQueryStep(urlKey, to, "push");
        },
        [registry, syncToUrl, urlKey],
    );

    const replace = useCallback(
        (to: LoginStepId) => {
            if (!registry[to]) return;
            setStepId(to);
            const st = stackRef.current;
            if (st.length === 0) st.push(to);
            else st[st.length - 1] = to;
            if (syncToUrl) writeQueryStep(urlKey, to, "replace");
        },
        [registry, syncToUrl, urlKey],
    );

    const back = useCallback(() => {
        const st = stackRef.current;
        if (st.length <= 1) return;
        st.pop();
        const prev = st[st.length - 1];
        setStepId(prev);
        if (syncToUrl) writeQueryStep(urlKey, prev, "replace");
    }, [syncToUrl, urlKey]);

    const Step = registry[stepId];

    return { stepId, Step, go, replace, back, data, setData };
}
